<template>
  <b-modal
    ref="inviteNewUserModal"
    hide-footer
    centered
    title="Invite new user"
    :busy="inProgress"
  >
    <b-form @submit.prevent="save">
      <b-form-group label="Role:">
        <b-form-select
          :options="availableRoles"
          v-model="$v.selectedRoles.$model"
          :class="{ 'is-invalid': $v.selectedRoles.$error }"
          :disabled="inProgress"
          multiple
        >
        </b-form-select>
        <b-form-invalid-feedback
          v-if="!$v.selectedRoles.isSelected"
          >
          At least one role must be specified
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group label="E-mail:">
        <b-form-input
          v-model="$v.email.$model"
          :class="{ 'is-invalid': $v.email.$error }"
          placeholder="example@domain.com"
          :disabled="inProgress"
        />
        <b-form-invalid-feedback
          v-if="!$v.email.required"
          >
          E-mail is required
        </b-form-invalid-feedback>
        <b-form-invalid-feedback
          v-if="!$v.email.email"
          >
          This field should contain a valid E-mail address
        </b-form-invalid-feedback>
        <b-form-invalid-feedback
          v-if="!$v.email.isUnique"
          >
          This E-mail address already registered
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group label="Display Name:">
        <b-form-input
          v-model="$v.displayName.$model"
          :class="{ 'is-invalid': $v.displayName.$error }"
          :disabled="inProgress"
        />
        <b-form-invalid-feedback
          v-if="!$v.displayName.required"
          >
          Display Name is required
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group label="Team Name:">
        <b-form-input
          v-model="$v.teamName.$model"
          :class="{ 'is-invalid': $v.teamName.$error }"
          :disabled="inProgress"
        />
        <b-form-invalid-feedback
          v-if="!$v.teamName.required"
          >
          Team Name is required
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group label="Website ID:">
        <b-form-input
          v-model="$v.websiteId.$model"
          :class="{ 'is-invalid': $v.websiteId.$error }"
          :disabled="inProgress"
        />
        <b-form-invalid-feedback
          v-if="!$v.websiteId.required"
          >
          Website ID is required
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group label="Password:" v-if="!autoPass">
        <b-form-input
          v-model="$v.pass.$model"
          :class="{ 'is-invalid': $v.pass.$error }"
          :disabled="inProgress"
        />
        <b-form-invalid-feedback
          v-if="!$v.pass.required"
          >
          Password is required
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group>
        <b-form-checkbox
          v-model="autoPass"
          :disabled="inProgress"
        >
          Generate password
        </b-form-checkbox>
      </b-form-group>
      <b-form-group>
        <b-form-checkbox
          v-model="invite"
          :disabled="inProgress"
        >
          Send invitation
        </b-form-checkbox>
      </b-form-group>
      <div>
        <b-button
          variant="primary"
          type="submit"
          :disabled="inProgress"
        >Save</b-button>
      </div>
    </b-form>
  </b-modal>
</template>

<script>
import { mapState } from 'vuex'
import { required, email } from 'vuelidate/lib/validators'
// helpers
import eventBus from '@/helpers/event-bus'
// services
import db from '@/services/db'
import auth from '@/services/auth'
import user from '@/services/user'

export default {
  props: ['users', 'dashboards'],
  data () {
    return {
      email: null,
      autoPass: true,
      pass: null,
      invite: true,
      availableRoles: [
        { value: 'read', text: 'Regular' },
        { value: 'update', text: 'Dashboard editor' },
        { value: 'manageUsers', text: 'User manager' }
      ],
      selectedRoles: ['read'],
      inProgress: false,
      displayName: null,
      teamName: null,
      websiteId: null,
      existingUsers: null
    }
  },
  computed: {
    ...mapState([
      'currentDash'
    ]),
    availableDashboards () {
      const items = []

      for (let dId in this.dashboards) {
        const dashboard = this.dashboards[dId]

        items.push({
          value: dId,
          text: dashboard.name,
        })
      }

      return items
    }
  },
  methods: {
    closeModal () {
      if (this.$refs['inviteNewUserModal']) {
        this.$refs['inviteNewUserModal'].hide()
      }
    },
    async save () {
      if (this.inProgress) return
      this.$v.$touch()
      if (this.$v.$invalid) return
      this.inProgress = true

      const userRules = {}

      for (let rule of this.selectedRoles) {
        userRules[rule] = true
      }

      const idToken = await auth.getIdToken()
      const vm = this
      const data = {
        email: vm.email,
        idToken,
        invite: vm.invite,
        dashboard: vm.currentDash,
        rules: userRules,
        displayName: vm.displayName,
        teamName: vm.teamName,
        websiteId: vm.websiteId
      }

      if (this.autoPass) data.pass = this.genPass(6)
      else data.pass = this.pass

      user.create(data)
        .then((res) => {
          if (res.data.status === 'OK') {
            this.inProgress = false
            this.email = null
            this.selectedRoles = ['read']
            this.pass = null
            this.autoPass = true
            this.invite = true
            this.displayName = null
            this.teamName = null
            this.websiteId = null

            this.$nextTick(() => {
              this.$v.$reset()
            })

            if (this.$refs['inviteNewUserModal']) {
              this.$refs['inviteNewUserModal'].hide()
            }
          } else {
            this.$store.commit('addError', res.data.err)

            this.inProgress = false
          }
        })
        .catch((err) => {
          this.inProgress = false
          console.error(err)
        })
    },
    genPass (length) {
      var text = ''
      var possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'

      for (var i = 0; i < length; i++) {
        text += possible.charAt(Math.floor(Math.random() * possible.length))
      }

      return text
    }
  },
  async created () {
    this.existingUsers = await user.getUserList()

    eventBus.$on('showInviteNewUserModal', () => {
      if (this.$refs['inviteNewUserModal']) {
        this.$refs['inviteNewUserModal'].show()
      }
    })
  },
  mounted () {
    if (this.$refs['inviteNewUserModal']) {
      $(this.$refs['inviteNewUserModal']['$el']).appendTo('body')
    }
  },
  beforeDestroy () {
    if (this.$refs['inviteNewUserModal']) {
      $(this.$refs['inviteNewUserModal']['$el']).remove()
    }
  },
  validations: {
    selectedRoles: {
      isSelected (val) {
        if (!val.length) return false
        return true
      }
    },
    email: {
      required,
      email,
      isUnique (val) {
        if (!this.existingUsers) return true

        for (let user of this.existingUsers) {
          if (user.email === val) return false
        }

        return true
      }
    },
    displayName: {
      required
    },
    teamName: {
      required
    },
    websiteId: {
      required
    },
    pass: {
      required (val) {
        if (this.autoPass) return true
        if (val && val.length) return true
        return false
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/Modals/InviteNewUser.vue